'use client';

// npm library imports
import { useCallback, useMemo, useState } from 'react';

// type imports
import type { CarouselImage } from '@lib/types';

// style imports
import styles from './imageGallery.module.scss';

// component imports
import SingleGalleryImage from './_SingleGalleryImage';
import ImageModal from './_ImageModal';

/**
 * Displays a gallery of images in a grid layout with a modal for full screen view on click
 *
 * @param props - The component props object
 * @param props.imagesArray The array of CarouselImages to display
 *
 * @returns ImageGallery component
 *
 * @example
 * ```tsx
 * <ImageGallery imagesArray={imagesArray} />
 * ```
 */
const ImageGallery = ({ imagesArray }: { imagesArray: CarouselImage[] }) => {
    // State for the current image index in the modal and modal toggle
    const [modalImage, setModalImage] = useState<number>(0);
    const [isModalOpen, setIsModalOpen] = useState(false);

    /**
     * Function to get the new image index when sliding the modal
     * 
     * @param slideRight - Boolean to determine if the slide is to the right
     * @returns The new image index
     */
    const getNewModalImageNum = useCallback(
        (slideRight: boolean): number => {
            if (slideRight) {
                return modalImage >= imagesArray.length - 1
                    ? 0
                    : modalImage + 1;
            } else {
                return modalImage === 0
                    ? imagesArray.length - 1
                    : modalImage - 1;
            }
        },
        [imagesArray.length, modalImage],
    );

    /**
     * Function to open modal when an image is clicked
     * 
     * @param index - The index of the image clicked
     * @returns void
     */
    const handleImageClick = (index: number) => {
        setModalImage(index);
        setIsModalOpen(true);
    };

    /**
     * Generate array of gallery groups, each containing up to 5 images.
     *
     * @returns Memoized array of gallery images, grouped as 2 then 3 images per row
     */
    const generateArray = useMemo(() => {
        // Length is calculated by dividing total images by 5 and rounding up
        return Array.from(
            { length: Math.ceil(imagesArray.length / 5) },
            (_, groupIndex) => {
                // Calculate starting index for current group of 5 images
                const startIndex = groupIndex * 5;

                // Get subset of images for this group (up to 5 images)
                const chunk = imagesArray.slice(startIndex, startIndex + 5);

                // First row: Create array of first 2 images in chunk
                const firstRow = chunk
                    .slice(0, 2)
                    .map(
                        (image, index) =>
                            image && (
                                <SingleGalleryImage
                                    key={startIndex + index}
                                    image={image}
                                    onClick={() =>
                                        handleImageClick(startIndex + index)
                                    }
                                />
                            ),
                    );

                // Second row: Create array of remaining 3 images in chunk
                const secondRow = chunk.slice(2, 5).map(
                    (image, index) =>
                        image && (
                            <SingleGalleryImage
                                key={startIndex + index + 2} // Add 2 to account for first row indices
                                image={image}
                                onClick={() =>
                                    handleImageClick(startIndex + index + 2)
                                }
                            />
                        ),
                );

                // Return group container with two rows
                // Each group forms a 2x3 grid (top row: 2 images, bottom row: 3 images)
                return (
                    <div key={groupIndex}>
                        <div className={styles.row}>{firstRow}</div>
                        <div className={styles.row}>{secondRow}</div>
                    </div>
                );
            },
        );
    }, [imagesArray]); // Only recalculate when imagesArray changes

    return (
        <div className={`${styles.continer} ${styles.gallerySmallTop}`}>
            {generateArray}
            <ImageModal
                image={imagesArray[modalImage]}
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                }}
                onNext={() => {
                    setModalImage(getNewModalImageNum(true));
                }}
                onPrev={() => {
                    setModalImage(getNewModalImageNum(false));
                }}
            />
        </div>
    );
};

export default ImageGallery;
